<script>
export default {
    inheritAttrs: false,
};
</script>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import BnContactEditDialog from '@/components/contacts/BnContactEditDialog.vue';
import { getRelationshipTypes, getRelationshipTypesInverse, findOrganizationRelationshipList } from '@/common/api/terminology.api';
import { EMERGENCY_CONTACT, NEXT_OF_KIN, LEGAL_GUARDIAN, POWER_OF_ATTORNEY, PORTAL_ACCESS } from '@/common/config';
import BnContactSearch from '@/components/BnContactSearch.vue';
import router from '@/router';
import BnInfo from '@/components/BnInfo.vue';
import BnAvatar from '@/components/BnAvatar.vue';
import DataProvider from '@/components/DataProvider.js';
import { Reference } from '@/fhirworks';
import { useAuthStore } from '@/stores/auth';
import { useContactFlow } from '@/common/workflowEngine/useContactFlow';
import { addRefToBackBtnListiner, removeRefToBackBtnListiner } from '@/common/nativeBackButton';
import { useGetDisplayResource } from '@/composables/useGetDisplayResource';
import PatientContact from '@/fhirworks/PatientContact';

const { getDisplay, getReference } = useGetDisplayResource();

const authStore = useAuthStore();

const emit = defineEmits(['addNewContactWithRelationshipCompleted', 'dataChanged', 'refreshGuarantor']);
const props = defineProps({
    subjectId: {
        type: String,
    },
    subjectResourceType: {
        type: String,
    },
    subject: {
        type: Object,
    },
    relatedId: {
        type: String,
    },
    related: {
        type: Object,
    },
    relationships: {
        type: Array,
    },
    addingNewContact: {
        type: Boolean,
        default: false,
    },
    newContactData: {
        type: Object,
    },
    isWidget: {
        type: Boolean,
        default: false,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
    isEmergencyContact: {
        type: Boolean,
        default: false,
    },
    isLegalGuardian: {
        type: Boolean,
        default: false,
    },
    existingRelationships: {
        type: Array,
        default: () => [],
    },
});

const workingProps = ref({
    primary: undefined,
    inverse: undefined,
    twoWay: true,
    subject: undefined,
    related: undefined,
    relationship: undefined,
    inverseRelationship: undefined,
    emergencyContact: undefined,
    nextOfKin: undefined,
    legalGuardian: undefined,
    powerOfAttorney: false,
});

const dpRelationship = ref([]);
const relationshipOptions = ref([]);
const relationshipInverse = ref([]);
const emergencyContactLabel = EMERGENCY_CONTACT;
const nextOfKinLabel = NEXT_OF_KIN;
const legalGuardianLabel = LEGAL_GUARDIAN;
const powerOfAttorneyLabel = POWER_OF_ATTORNEY;
const portalAccessLabel = PORTAL_ACCESS;
const searchError = ref(null);

const subjectDisplay = ref('');
const relatedDisplay = ref('');
const newContactDisplay = ref('');

const isPatientContact = computed(() => props.related instanceof PatientContact);
const effectiveReadOnly = computed(() => props.readOnly || isPatientContact.value);

onMounted(() => {
    getRelationshipTypes().then((relationshipTypes) => {
        if (relationshipTypes.length) {
            relationshipOptions.value = relationshipTypes
                ?.sort((a, b) => {
                    if (a.resource.display > b.resource.display) return 1;
                    return -1;
                })
                .map((e) => ({ display: e.resource.display, system: e.resource.system, code: e.resource.code }));
            // Filter out both the special relationship codes and any option with display "Other"
            relationshipOptions.value = relationshipOptions.value.filter((item) => !['C', 'N', 'G', 'P'].includes(item.code) && item.display !== 'Other');
        }
    });
    getRelationshipTypesInverse().then((relationshipTypes) => {
        relationshipInverse.value = relationshipTypes;
    });
});

watch(
    () => workingProps.value.subject,
    async (newVal) => {
        let label = dpRelationship.value.find((item) => item.resourceType === newVal.resourceType && item.id === newVal.id);
        subjectDisplay.value = await getDisplay(label || newVal);
    },
);

watch(
    () => workingProps.value.related,
    async (newVal) => {
        if (newVal instanceof PatientContact) {
            relatedDisplay.value = [newVal.name?.firstName, newVal.name?.lastName].filter(Boolean).join(' ');
            return;
        }
        let label = dpRelationship.value.find((item) => item.resourceType === newVal.resourceType && item.id === newVal.id);
        relatedDisplay.value = await getDisplay(label || newVal);
    },
);

watch(
    () => props.newContactData,
    async (newVal) => {
        let label = dpRelationship.value.find((item) => item.resourceType === newVal.resourceType && item.id === newVal.id);
        newContactDisplay.value = await getDisplay(label || newVal);
    },
    { deep: true, immediate: true },
);

const dpRelationshipQuery = computed(() => {
    if (props.subject && props.related) {
        return { resource: [...props.relationships.filter((it) => it), props.subject, props.related] };
    }

    if (props.subjectId && props.relatedId) {
        return {
            query: [
                { query: '_include=subject&subject=' + props.subjectId + '&related=' + props.relatedId, resourceType: 'BN_Relationship' },
                { query: '_include=subject&subject=' + props.relatedId + '&related=' + props.subjectId, resourceType: 'BN_Relationship' },
            ],
        };
    }

    workingProps.value.subject = { id: props.subjectId, resourceType: props.subjectResourceType };
    if (props.addingNewContact) {
        workingProps.value.related = new Reference({ id: undefined, resourceType: 'Patient', display: 'New patient' });
        workingProps.value.newRelationship = true;
    }
    return [];
});
const primaryRelationshipLabel = computed(() => {
    const contact = subjectDisplay.value;
    const related = props.addingNewContact ? newContactDisplay.value : relatedDisplay.value;
    return related + ' is ' + contact + "'s";
});
const inverseRelationshipLabel = computed(() => {
    const contact = subjectDisplay.value;
    const related = props.addingNewContact ? newContactDisplay.value : relatedDisplay.value;
    return contact + ' is ' + related + "'s";
});
const relatedReference = computed(() => {
    return workingProps.value.related;
});
const isSeveredRelationship = computed(() => {
    return !(workingProps.value.newRelationship || props.addingNewContact) && !workingProps.value?.primary?.active;
});
const mainPatient = computed(() => {
    return dpRelationship?.value.find((el) => el.resourceType === 'Patient' && el.id === props.subjectId);
});
const refId = (ref) => ref?.id ?? ref?.reference?.split('/')[1];
const refResourceType = (ref) => ref?.resourceType ?? ref?.reference?.split('/')[0];

const loadRelationship = function () {
    if (isPatientContact.value) {
        const rel = props.related.relationship;
        const codeOf = (r) => r?.coding?.[0]?.code;
        workingProps.value = {
            primary: undefined,
            inverse: undefined,
            twoWay: false,
            subject: props.subject,
            related: props.related,
            relationship: rel?.find((r) => codeOf(r) && !['C', 'N', 'G', 'P'].includes(codeOf(r)))?.coding?.[0],
            inverseRelationship: undefined,
            emergencyContact: rel?.some((r) => codeOf(r) === 'C'),
            nextOfKin: rel?.some((r) => codeOf(r) === 'N'),
            legalGuardian: rel?.some((r) => codeOf(r) === 'G'),
            powerOfAttorney: rel?.some((r) => codeOf(r) === 'P'),
        };
        return;
    }
    const primary = dpRelationship.value.find((item) => item.resourceType === 'BN_Relationship' && refId(item.subject) === props.subjectId) || undefined;
    if (!primary?.id) return;

    const inverse = dpRelationship.value.find((item) => item.resourceType === 'BN_Relationship' && refId(item.subject) === props.relatedId) || undefined;

    workingProps.value = {
        primary: primary,
        inverse: inverse,
        twoWay: !!inverse?.id,
        subject: primary.subject,
        related: primary.related,
        portalAccess: primary.portalAccess,
        relationship: primary.relationship?.find((item) => item.code && !['C', 'N', 'G', 'P'].includes(item.code)),
        inverseRelationship: inverse?.relationship?.find((item) => item.code && !['C', 'N', 'G', 'P'].includes(item.code)),
        emergencyContact: primary.relationship?.some((a) => a.code === 'C'),
        nextOfKin: primary.relationship?.some((a) => a.code === 'N'),
        legalGuardian: primary.relationship?.some((a) => a.code === 'G'),
        powerOfAttorney: primary.relationship?.some((a) => a.code === 'P'),
    };
};
// used in the contact edit dialog
const saveNewContactRelationship = async function (contact) {
    workingProps.value.related.id = contact.id;
    workingProps.value.related.display = contact.fullName;
    workingProps.value.related.resourceType = contact.resourceType;
    return await saveRelationship();
};
const saveRelationship = async function () {
    const { saveRelationship: saveRelationshipFlow } = useContactFlow();
    emit('dataChanged', workingProps.value);
    emit('refreshGuarantor', true);
    return await saveRelationshipFlow(workingProps.value);
};
const openContact = function (item) {
    router.push({ name: item.resourceType.toLowerCase(), params: { id: item.id, activeTab: 'contact' } });
};
const contactEditDialog = ref(null);
const contactSearch = ref(null);
const dpRelationshipRef = ref(null);
const bnDialog = ref(null);
const addContact = function (item) {
    const options = {
        header: 'Add contact',
        persistent: true,
    };
    const localProps = {
        add: item,
        addNewRelatedContact: true,
        relationSubject: workingProps.value.subject,
        isEmergencyContact: props.isEmergencyContact,
    };
    addRefToBackBtnListiner(contactEditDialog.value, 'close', 'contactEditDialog');
    contactEditDialog.value.show(options, localProps).then(async (resource) => {
        removeRefToBackBtnListiner('contactEditDialog');
        if (resource) {
            emit('addNewContactWithRelationshipCompleted', resource);
            dpRelationshipRef.value?.refresh();
        }
    });
};
const selectContact = async function (contact) {
    // disallow self-relationships
    if (contact.id === workingProps.value.subject?.id) {
        searchError.value = 'Sorry, you cannot self-relate a contact!';
        window.setTimeout(() => {
            contactSearch.value.focus();
            searchError.value = null;
        }, 3000);
        return;
    }
    // set contact resources
    dpRelationshipRef?.value.addItem(contact);

    if (props.isEmergencyContact) {
        workingProps.value.emergencyContact = true;
    }

    if (props.isLegalGuardian) {
        workingProps.value.legalGuardian = true;
    }

    // rebuild workingProps
    workingProps.value = {
        ...workingProps.value,
        related: { ...(await getReference(contact, true)), photo: contact.photo },
        twoWay: true,
        newRelationship: true,
    };

    dpRelationshipRef.value?.refresh();
};
// used in the contact edit dialog
const deleteRelationship = async function () {
    // let sameGuarantor = false;
    const options = {
        header: 'Delete Relationship',
        warning: workingProps.value.twoWay ? 'Both sides of this two-way relationship will be deleted!' : undefined,
        message: 'Are you sure you want to delete this relationship?',
        confirm: 'Yes',
        cancel: 'No',
    };

    let message = 'Guarantor will be reverted back to Self from ' + workingProps?.value.related.display + '.';
    if (mainPatient?.value?.Guarantor?.id === workingProps?.value.related.id) {
        if (options.warning === undefined) {
            options.warning = message;
        } else {
            options.warning = [options.warning];
            options.warning.push(message);
        }
        // sameGuarantor = true;
    }

    return bnDialog.value.show(options).then(async (confirm) => {
        if (!confirm) return;
        const { deleteRelationship } = useContactFlow();
        emit('refreshGuarantor', true);
        return await deleteRelationship(workingProps.value);
    });
};
// used in the contact edit dialog
const unseverRelationship = async function () {
    const options = {
        header: 'Unsever relationship',
        warning: workingProps.value.twoWay ? 'Both sides of this two-way relationship will be unsevered!' : undefined,
        message: 'Are you sure you want to unsever this relationship?',
        confirm: 'Yes',
        cancel: 'No',
    };
    return bnDialog.value.show(options).then(async (confirm) => {
        if (!confirm) return;
        const { unseverRelationship } = useContactFlow();
        emit('dataChanged');
        emit('refreshGuarantor', true);
        return await unseverRelationship(workingProps.value);
    });
};
// used in the contact edit dialog
const severRelationship = async function () {
    let sameGuarantor = false;
    let severWarnings = [];
    if (workingProps.value.twoWay) {
        severWarnings.push('Both sides of this two-way relationship will be severed!');
    }
    if (workingProps.value.emergencyContact) {
        severWarnings.push('The emergency contact setting will be cleared.');
    }
    if (workingProps.value.nextOfKin) {
        severWarnings.push('The next of kin setting will be cleared.');
    }
    if (workingProps.value.portalAccess) {
        severWarnings.push('The portal access setting will be cleared.');
    }
    if (workingProps.value.legalGuardian) {
        severWarnings.push('The legal guardian setting will be cleared.');
    }
    if (workingProps.value.powerOfAttorney) {
        severWarnings.push('The power of attorney setting will be cleared.');
    }
    if (mainPatient?.value?.Guarantor?.id === workingProps?.value.related.id) {
        severWarnings.push('Guarantor will be reverted back to Self from ' + workingProps?.value.related.display + '.');
        sameGuarantor = true;
    }
    const options = {
        header: 'Sever relationship',
        warning: severWarnings,
        message: 'Are you sure you want to sever this relationship?',
        confirm: 'Yes',
        cancel: 'No',
    };
    return bnDialog.value.show(options).then(async (confirm) => {
        if (!confirm) return;
        if (sameGuarantor) {
            mainPatient.value.Guarantor = {
                display: 'Self',
            };
            await dpRelationshipRef?.value.save(mainPatient.value);
        }
        const { severRelationship } = useContactFlow();
        emit('refreshGuarantor', true);
        return await severRelationship(workingProps.value);
    });
};
const setInverseRelationship = function (relationship) {
    // If no relationship is selected or two-way is disabled, clear the inverse relationship
    if (!relationship || !workingProps.value.twoWay) {
        workingProps.value.inverseRelationship = undefined;
        return;
    }

    // For custom relationships (code 2131-1), use the same display text
    if (relationship.code === '2131-1') {
        workingProps.value.inverseRelationship = {
            system: relationship.system,
            code: relationship.code,
            display: relationship.display,
        };
        return;
    }

    // Try to find the inverse relationship in the mapping
    const inverse = relationshipInverse.value.find((item) => {
        // Check if the item has the expected structure
        return item.code === relationship.code && item.inverse;
    });

    if (inverse && inverse.inverse) {
        // If we found a mapped inverse relationship, use it
        workingProps.value.inverseRelationship = {
            system: relationship.system,
            code: inverse.inverse.code,
            display: inverse.inverse.display,
        };
    } else {
        // For unmapped relationships, clear the inverse
        workingProps.value.inverseRelationship = undefined;
    }

    // Auto save if in widget mode
    if (props.isWidget) {
        saveRelationship();
    }
};
const getRelationshipOptions = function (a, b) {
    let options;
    if (a?.resourceType === 'Organization' || b?.resourceType === 'Organization') {
        options = findOrganizationRelationshipList(a.resourceType, b.resourceType);
    } else {
        options = relationshipOptions.value;
    }

    // Filter out the "Other" option by display name
    return options.filter((option) => option.display !== 'Other');
};
const autoSaveRelationship = async function () {
    if (props.isWidget) {
        await saveRelationship();
    }
};

const refresh = function () {
    emit('dataChanged', workingProps.value);
};

const searchFilter = function (searchItem) {
    let itemExist = props.existingRelationships.find((item) => refId(item.related) === searchItem.id && refResourceType(item.related) === searchItem.resourceType);
    return !itemExist;
};

watch(
    () => workingProps,
    () => {
        emit('dataChanged', workingProps.value);
    },
    { immediate: true, deep: true },
);

const handleCustomRelationship = function (event) {
    // Get the current input value
    const inputValue = event.target.value;

    // If there's text in the input, create a custom relationship
    if (inputValue) {
        // Create a custom relationship with "Other" code
        workingProps.value.relationship = {
            system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
            code: '2131-1', // Using "Other" code
            display: inputValue,
        };

        // If two-way relationship is enabled and no inverse relationship exists,
        // also update the inverse relationship
        if (workingProps.value.twoWay && !workingProps.value.inverseRelationship) {
            workingProps.value.inverseRelationship = {
                system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
                code: '2131-1', // Using "Other" code
                display: inputValue,
            };
        }

        // Auto save
        if (props.isWidget) {
            saveRelationship();
        }

        // Blur the input to close the dropdown
        event.target.blur();
    }
};

const handleCustomInverseRelationship = function (event) {
    // Get the current input value
    const inputValue = event.target.value;

    // If there's text in the input, create a custom relationship
    if (inputValue) {
        workingProps.value.inverseRelationship = {
            system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
            code: '2131-1', // Using "Other" code
            display: inputValue,
        };

        // Auto save
        if (props.isWidget) {
            saveRelationship();
        }

        // Blur the input to close the dropdown
        event.target.blur();
    }
};

const handleEnterKey = function (event) {
    // Get the current input value
    const inputValue = event.target.value;

    // If there's text in the input, create a custom relationship
    if (inputValue) {
        workingProps.value.relationship = {
            system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
            code: '2131-1', // Using "Other" code
            display: inputValue,
        };

        // If two-way relationship is enabled, also update the inverse relationship
        if (workingProps.value.twoWay) {
            workingProps.value.inverseRelationship = {
                system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
                code: '2131-1', // Using "Other" code
                display: inputValue,
            };
        }

        // Auto save
        if (props.isWidget) {
            saveRelationship();
        }

        // Blur the input to close the dropdown
        event.target.blur();
    }
};

const handleInverseEnterKey = function (event) {
    // Get the current input value
    const inputValue = event.target.value;

    // If there's text in the input, create a custom relationship
    if (inputValue) {
        workingProps.value.inverseRelationship = {
            system: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
            code: '2131-1', // Using "Other" code
            display: inputValue,
        };

        // Auto save
        if (props.isWidget) {
            saveRelationship();
        }

        // Blur the input to close the dropdown
        event.target.blur();
    }
};

defineExpose({ saveRelationship, severRelationship, unseverRelationship, saveNewContactRelationship, deleteRelationship });
</script>

<template>
    <DataProvider ref="dpRelationshipRef" v-model="dpRelationship" collection v-bind="dpRelationshipQuery" @loaded="loadRelationship" />

    <v-card flat v-bind="$attrs" :class="{ 'scroll-on': !isWidget }" :border="false">
        <div v-if="!workingProps.related && !isWidget">
            <BnContactSearch
                ref="contactSearch"
                :autofocus="true"
                placeholder="Search/add contacts"
                relationship-search
                :filter="searchFilter"
                data-cy="relationshipSearch"
                @add-new-clicked="addContact"
                @item-clicked="selectContact"
            />
            <v-alert v-if="searchError" type="error" text dismissible class="mt-4">{{ searchError }}</v-alert>
        </div>

        <template v-else>
            <template v-if="props.newContactData">
                <div class="bn-section-header">Relationship information</div>
            </template>
            <template v-else>
                <v-list-item class="pa-3 contact-card">
                    <template v-if="!effectiveReadOnly" #prepend>
                        <BnAvatar class="usprivacy" :resource="relatedReference || workingProps.related" size="46" icon-size="sm" :link="!isWidget" @link="openContact(workingProps.related)" />
                    </template>
                    <v-list-item-title class="usprivacy text-h3 pl-2">
                        {{ workingProps.related?.display ?? relatedDisplay }}
                    </v-list-item-title>
                </v-list-item>
            </template>
            <!-- Primary Relationship -->
            <div class="relationshipDialog" style="max-width: 414px">
                <div data-cy="relationshipName" class="usprivacy font-weight-bold">{{ primaryRelationshipLabel }}</div>

                <v-autocomplete
                    v-model="workingProps.relationship"
                    label="Relationship"
                    :readonly="effectiveReadOnly"
                    :disabled="isSeveredRelationship"
                    :items="getRelationshipOptions(workingProps.subject, workingProps.related)"
                    item-title="display"
                    item-value="code"
                    return-object
                    :clearable="!effectiveReadOnly"
                    hide-details
                    data-cy="relationshipSelectBox"
                    class="mt-2 pt-0"
                    allow-overflow
                    auto-select-first
                    hide-no-data
                    hide-selected
                    :custom-filter="() => true"
                    @update:model-value="setInverseRelationship"
                    @update:search="autoSaveRelationship"
                    @blur="handleCustomRelationship"
                    @keydown.enter.prevent="handleEnterKey"
                >
                </v-autocomplete>

                <template v-if="workingProps.subject?.resourceType === 'Patient' && workingProps.related?.resourceType !== 'Organization'">
                    <div class="d-flex flex-column">
                        <div class="d-flex align-center">
                            <v-checkbox
                                v-model="workingProps.emergencyContact"
                                :readonly="effectiveReadOnly"
                                :disabled="isSeveredRelationship || isEmergencyContact"
                                data-cy="emergencyContactCheckbox"
                                class="mt-1 align-self-center"
                                hide-details
                                @update:model-value="autoSaveRelationship"
                            >
                                <template #label> <font-awesome-icon class="mr-1" :icon="['far', 'truck-medical']" :disabled="isSeveredRelationship" /> {{ emergencyContactLabel }} </template>
                            </v-checkbox>
                        </div>
                        <div class="d-flex align-center">
                            <v-checkbox
                                v-model="workingProps.legalGuardian"
                                :readonly="effectiveReadOnly"
                                :disabled="isSeveredRelationship"
                                class="mt-1 align-self-center"
                                hide-details
                                data-cy="legalGuardianCheckbox"
                                @update:model-value="autoSaveRelationship"
                            >
                                <template #label> <font-awesome-icon class="mr-2" :icon="['far', 'gavel']" />{{ legalGuardianLabel }}</template>
                            </v-checkbox>
                        </div>
                        <div class="d-flex align-center">
                            <v-checkbox
                                v-model="workingProps.powerOfAttorney"
                                :readonly="effectiveReadOnly"
                                :disabled="isSeveredRelationship"
                                class="mt-1 align-self-center"
                                hide-details
                                @update:model-value="autoSaveRelationship"
                            >
                                <template #label> <font-awesome-icon class="mr-2" :icon="['far', 'pen-alt']" /> {{ powerOfAttorneyLabel }} </template>
                            </v-checkbox>
                        </div>
                        <div class="d-flex align-center">
                            <v-checkbox
                                v-model="workingProps.nextOfKin"
                                :readonly="effectiveReadOnly"
                                :disabled="isSeveredRelationship"
                                class="mt-1 align-self-center"
                                hide-details
                                @update:model-value="autoSaveRelationship"
                            >
                                <template #label> <font-awesome-icon class="mr-2" :icon="['fas', 'people-pants-simple']" /> {{ nextOfKinLabel }} </template>
                            </v-checkbox>
                        </div>
                        <div class="d-flex align-center">
                            <v-checkbox
                                v-model="workingProps.portalAccess"
                                :readonly="effectiveReadOnly"
                                :disabled="isSeveredRelationship || !authStore.portalEnabled"
                                class="mt-1 align-self-center"
                                hide-details
                                @update:model-value="autoSaveRelationship"
                            >
                                <template #label> <font-awesome-icon class="mr-2" :icon="['fas', 'globe-americas']" /> {{ portalAccessLabel }} </template>
                            </v-checkbox>
                        </div>
                    </div>
                </template>

                <!-- Inverse Relationship -->
                <div v-if="!isWidget" class="d-flex">
                    <v-switch
                        v-model="workingProps.twoWay"
                        :disabled="isSeveredRelationship"
                        label="Two-way relationship"
                        hide-details
                        class="mt-0 ml-2"
                        @update:model-value="setInverseRelationship(workingProps.relationship)"
                    ></v-switch>
                    <BnInfo type="dialog"> In a two-way relationship a related contact will be added to both parties. </BnInfo>
                </div>
                <template v-if="workingProps.twoWay && !isWidget">
                    <div class="text-subtitle-1 font-weight-bold mt-2 usprivacy">{{ inverseRelationshipLabel }}</div>
                    <v-autocomplete
                        v-model="workingProps.inverseRelationship"
                        :disabled="isSeveredRelationship"
                        label="Relationship"
                        :items="getRelationshipOptions(workingProps.related, workingProps.subject)"
                        item-title="display"
                        item-value="code"
                        return-object
                        clearable
                        class="mt-2 pt-0"
                        allow-overflow
                        auto-select-first
                        hide-no-data
                        hide-selected
                        :custom-filter="() => true"
                        @blur="handleCustomInverseRelationship"
                        @keydown.enter.prevent="handleInverseEnterKey"
                    ></v-autocomplete>
                </template>
            </div>
        </template>
    </v-card>
    <BnContactEditDialog v-if="authStore" ref="contactEditDialog" :auth-store="authStore" @data-changed="refresh" />
    <BnDialog ref="bnDialog" />
</template>
<style>
.contact-card {
    background-color: #f4f4f4;
    border-bottom: 1px #989898 solid;
    margin-bottom: 8px;
    margin-top: 14px;
}
</style>
